import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { DateInput } from './ui/date-input';
import { APIProvider } from '@vis.gl/react-google-maps';
import AddressAutocomplete from './clients/AddressAutocomplete';
import { UserPlus, FileText, ShieldCheck, Sparkles, Upload, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const CONSENT_FIELDS = [
  { key: 'operativo', label: 'Gestione del rapporto/servizio e adempimenti di legge', required: true },
  { key: 'marketing', label: 'Comunicazioni informative e commerciali (newsletter)' },
  { key: 'profilazione', label: 'Profilazione per offerte personalizzate' },
];

function BirthComuneInput({ value, onSelect }) {
  const [opts, setOpts] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const search = async (t) => {
    onSelect((t || '').toUpperCase(), null);
    if (!t || t.length < 2) { setOpts([]); setOpen(false); return; }
    try {
      const r = await axios.get(`${API}/utils/comuni/search`, { params: { q: t, limit: 8 } });
      setOpts(Array.isArray(r.data) ? r.data : []);
      setOpen((r.data || []).length > 0);
    } catch (_) { setOpts([]); }
  };
  return (
    <div className="relative" ref={ref}>
      <Input value={value} onChange={(e) => search(e.target.value)} placeholder="Comune di nascita" className="uppercase" />
      {open && opts.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto">
          {opts.map((c, i) => (
            <div key={i} className="px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-100"
              onClick={() => { onSelect((c.nome || '').toUpperCase(), c.provincia || ''); setOpen(false); }}>
              {c.nome} <span className="text-slate-400 text-xs">({c.provincia})</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Estrazione automatica dati dal documento d'identità (come Canone RAI / Ospitalità): stessi
// document_type di /api/ocr/extract-client-data e stesse sigle dei "Documenti anagrafici".
const DOC_TIPI_OCR = [
  { label: "Carta d'identità", ocr: 'carta_identita', sigla: 'CIE' },
  { label: 'Patente', ocr: 'patente', sigla: 'PAT' },
  { label: 'Passaporto', ocr: 'passaporto', sigla: 'PASS' },
  { label: 'Permesso di soggiorno', ocr: 'permesso_soggiorno', sigla: 'PSE' },
  { label: 'Visura camerale (azienda)', ocr: 'visura_camerale', sigla: 'VISURA', azienda: true },
];

const EMPTY = {
  is_company: false,
  last_name: '', first_name: '', gender: '',
  birth_date: '', birth_comune: '', birth_provincia: '', birth_abroad: false, birth_country: '',
  fiscal_code: '',
  company_name: '', vat_number: '', pec: '',
  company_address: '', company_address_number: '', company_city: '', company_provincia: '', company_postal_code: '',
  address: '', address_number: '', city: '', city_provincia: '', postal_code: '',
  phone: '', email: '',
};

/**
 * Mini-form di REGISTRAZIONE nuovo cliente (nessuna modifica).
 * props: prefillName, prefill (dati già estratti da un documento, es. Scanner Documenti),
 *        estrazione (riquadro "Estrazione automatica dati", default sì se non c'è prefill),
 *        onCreated(client), onCancel()
 */
export default function NewClientForm({ prefillName = '', prefill = null, estrazione = !prefill, onCreated, onCancel }) {
  const [nc, setNc] = useState(() => {
    const base = { ...EMPTY, last_name: (prefillName || '').trim().toUpperCase() };
    if (!prefill) return base;
    const valori = Object.fromEntries(
      Object.entries(prefill).filter(([k, v]) => k in EMPTY && v !== null && v !== undefined && v !== '')
    );
    return { ...base, ...valori };
  });
  const [saving, setSaving] = useState(false);
  const [docTipo, setDocTipo] = useState(DOC_TIPI_OCR[0].ocr);
  const [estraendo, setEstraendo] = useState(false);
  const [documenti, setDocumenti] = useState([]); // da allegare in anagrafica dopo la creazione
  const [esistente, setEsistente] = useState(null); // cliente già registrato con lo stesso CF

  const allegaDocumento = async (clientId, doc, cognome, nome) => {
    const fd = new FormData();
    fd.append('file', doc.file);
    fd.append('description', (doc.sigla === 'VISURA' ? `VISURA ${nc.company_name || ''}` : `${doc.sigla} ${cognome || ''} ${nome || ''}`).replace(/\s+/g, ' ').trim().toUpperCase());
    fd.append('category', 'anagrafico');
    if (doc.scadenza) fd.append('expiry_date', doc.scadenza);
    await axios.post(`${API}/clients/${clientId}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  };

  // cliente già registrato? privato: stesso CF tra i privati; azienda: stessa P.IVA tra le aziende
  // (il CF di un'azienda è quello del referente, che può essere anche cliente privato)
  const cercaPerCf = async (cf, azienda = false, piva = '') => {
    const chiave = azienda ? (piva || '').replace(/\D/g, '') : cf;
    if (!chiave) return null;
    try {
      const r = await axios.get(`${API}/clients`, { params: { page: 1, page_size: 10, search: chiave } });
      return (r.data.items || []).find((c) => (azienda
        ? c.is_company && (c.vat_number || '').replace(/\D/g, '') === chiave
        : !c.is_company && (c.fiscal_code || '').toUpperCase() === chiave.toUpperCase())) || null;
    } catch (_) {
      return null;
    }
  };

  // Carica il documento → compila i campi (si possono sempre correggere a mano). Un secondo
  // documento (es. Permesso di soggiorno dopo il Passaporto) completa solo i campi ancora vuoti.
  const estraiDaDocumento = async (file) => {
    if (!file) return;
    const tipo = DOC_TIPI_OCR.find((t) => t.ocr === docTipo);
    setEstraendo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('document_type', tipo.ocr);
      let d;
      let c;
      if (tipo.azienda) {
        // visura camerale: dati dell'azienda e del legale rappresentante (referente)
        const res = await axios.post(`${API}/ocr/extract`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        d = res.data.extracted_data || {};
        c = {
          company_name: d.company_name, vat_number: (d.vat_number || '').replace(/\D/g, ''), pec: (d.pec || '').toLowerCase(),
          company_address: d.company_address, company_address_number: d.company_address_number,
          company_city: d.company_city, company_provincia: d.company_provincia, company_postal_code: d.company_postal_code,
          last_name: d.last_name, first_name: d.first_name, fiscal_code: d.fiscal_code, birth_date: d.birth_date,
        };
      } else {
        const res = await axios.post(`${API}/ocr/extract-client-data`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        d = res.data.extracted_data || {};
        c = res.data.client_data || {};
      }
      if (!c.fiscal_code && !c.last_name && !c.first_name && !c.company_name && !c.vat_number) {
        toast.error('Nessun dato riconosciuto: verifica di aver scelto il tipo documento corretto');
        return;
      }
      let unito = { ...nc };
      Object.keys(EMPTY).forEach((k) => {
        if (k === 'is_company') return; // il documento è del referente: Privato/Azienda non si tocca
        const v = c[k];
        if (v === undefined || v === null || v === '') return;
        if (typeof v === 'boolean' || !unito[k]) unito[k] = typeof v === 'string' && k !== 'pec' ? v.toUpperCase() : v;
      });
      if (tipo.azienda) unito.is_company = true; // la visura è di un'azienda
      if (unito.birth_abroad) unito = { ...unito, birth_comune: '', birth_provincia: '' };
      // CAP non stampato sulla CIE: se comune e provincia ne individuano uno solo, lo compila
      if (!unito.postal_code && unito.city && unito.city_provincia) {
        try {
          const capRes = await axios.get(`${API}/cap`, { params: { city: unito.city, provincia: unito.city_provincia } });
          if (capRes.data.single && capRes.data.auto_cap) unito = { ...unito, postal_code: capRes.data.auto_cap };
        } catch (_) { /* aiuto in più */ }
      }
      // Passaporto/PSE spesso senza CF italiano: lo calcola se ci sono tutti i dati
      if (!unito.fiscal_code && unito.last_name && unito.first_name && unito.gender && unito.birth_date &&
          ((!unito.birth_abroad && unito.birth_comune && unito.birth_provincia) || (unito.birth_abroad && unito.birth_country))) {
        try {
          const cfRes = await axios.post(`${API}/calculate-fiscal-code`, {
            surname: unito.last_name, name: unito.first_name, birth_date: unito.birth_date, gender: unito.gender,
            birth_abroad: unito.birth_abroad, birth_comune: unito.birth_comune,
            birth_provincia: unito.birth_provincia, birth_country: unito.birth_country,
          });
          if (cfRes.data.fiscal_code) unito = { ...unito, fiscal_code: cfRes.data.fiscal_code };
        } catch (_) { /* aiuto in più */ }
      }
      setNc(unito);
      const doc = { file, sigla: tipo.sigla, scadenza: d.expiry_date || '' };
      setDocumenti((prev) => [...prev.filter((x) => x.sigla !== tipo.sigla), doc]);
      const trovato = await cercaPerCf(unito.fiscal_code, unito.is_company, unito.vat_number);
      setEsistente(trovato);
      if (trovato) {
        toast.warning(`${trovato.last_name} ${trovato.first_name} è già in anagrafica`);
      } else {
        toast.success(`Dati letti da ${tipo.label}: controlla e premi Crea cliente`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Estrazione dati non riuscita: compila i campi a mano');
    } finally {
      setEstraendo(false);
    }
  };

  // CF già registrato: si usa il cliente esistente (allegando i documenti caricati) invece di
  // crearne un doppione
  const usaEsistente = async () => {
    try {
      for (const doc of documenti) await allegaDocumento(esistente.id, doc, esistente.last_name, esistente.first_name);
    } catch (_) {
      toast.error('Cliente selezionato, ma il documento non è stato allegato in anagrafica');
    }
    onCreated && onCreated(esistente);
  };
  const [consents, setConsents] = useState({ operativo: true, marketing: false, profilazione: false });
  const [informativa, setInformativa] = useState(null);
  const [showInformativa, setShowInformativa] = useState(false);

  const toggleInformativa = async () => {
    if (!showInformativa && !informativa) {
      try {
        const r = await axios.get(`${API}/clients/informativa`);
        setInformativa(r.data);
      } catch (_) { toast.error('Informativa non disponibile'); }
    }
    setShowInformativa((v) => !v);
  };

  const calcCF = async () => {
    if (!nc.last_name || !nc.first_name || !nc.birth_date || !nc.gender) {
      toast.error('Compila Cognome, Nome, Sesso e Data di nascita'); return;
    }
    if (!nc.birth_abroad && (!nc.birth_comune || !nc.birth_provincia)) {
      toast.error('Compila Comune e Provincia di nascita'); return;
    }
    if (nc.birth_abroad && !nc.birth_country) { toast.error('Indica la Nazione di nascita'); return; }
    try {
      const res = await axios.post(`${API}/calculate-fiscal-code`, {
        surname: nc.last_name, name: nc.first_name, birth_date: nc.birth_date, gender: nc.gender,
        birth_abroad: nc.birth_abroad, birth_comune: nc.birth_comune,
        birth_provincia: nc.birth_provincia, birth_country: nc.birth_country,
      });
      setNc((x) => ({ ...x, fiscal_code: res.data.fiscal_code }));
      toast.success('Codice Fiscale calcolato');
    } catch (e) {
      toast.error('Errore nel calcolo del Codice Fiscale');
    }
  };

  const handleCreate = async () => {
    if (nc.is_company ? !nc.company_name.trim() : !nc.last_name.trim()) {
      toast.error(nc.is_company ? 'Ragione sociale obbligatoria' : 'Cognome obbligatorio');
      return;
    }
    setSaving(true);
    try {
      const common = {
        fiscal_code: nc.fiscal_code.trim().toUpperCase(),
        phone: nc.phone.trim(),
        email: nc.email.trim(),
        address: nc.address.trim().toUpperCase(),
        address_number: nc.address_number.trim(),
        city: nc.city.trim().toUpperCase(),
        city_provincia: nc.city_provincia.trim().toUpperCase(),
        postal_code: nc.postal_code.trim(),
      };
      const payload = nc.is_company
        ? { ...common, is_company: true, company_name: nc.company_name.trim().toUpperCase(), vat_number: nc.vat_number.trim(),
            pec: nc.pec.trim().toLowerCase(),
            company_address: nc.company_address.trim().toUpperCase(), company_address_number: nc.company_address_number.trim(),
            company_city: nc.company_city.trim().toUpperCase(), company_provincia: nc.company_provincia.trim().toUpperCase(),
            company_postal_code: nc.company_postal_code.trim(),
            // referente / legale rappresentante
            last_name: nc.last_name.trim().toUpperCase(), first_name: nc.first_name.trim().toUpperCase() }
        : { ...common,
            last_name: nc.last_name.trim().toUpperCase(),
            first_name: nc.first_name.trim().toUpperCase(),
            gender: nc.gender,
            birth_date: nc.birth_date || '',
            birth_abroad: nc.birth_abroad,
            birth_comune: nc.birth_comune.trim().toUpperCase(),
            birth_provincia: nc.birth_provincia.trim().toUpperCase(),
            birth_country: nc.birth_country.trim().toUpperCase() };
      payload.consents = { ...consents, operativo: true };
      const res = await axios.post(`${API}/clients`, payload);
      toast.success('Cliente creato');
      try {
        for (const doc of documenti) await allegaDocumento(res.data.id, doc, payload.last_name, payload.first_name);
      } catch (_) {
        toast.error('Cliente creato, ma il documento non è stato allegato in anagrafica');
      }
      onCreated && onCreated(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nella creazione del cliente');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-3 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-blue-800 flex items-center gap-1.5"><UserPlus className="w-4 h-4" /> Nuovo cliente</span>
        <div className="flex items-center gap-2 text-xs">
          <span className={!nc.is_company ? 'font-medium text-slate-900' : 'text-slate-400'}>Privato</span>
          <Switch checked={nc.is_company} onCheckedChange={(v) => setNc({ ...nc, is_company: v })} />
          <span className={nc.is_company ? 'font-medium text-slate-900' : 'text-slate-400'}>Azienda</span>
        </div>
      </div>

      {estrazione && (
        <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 space-y-2">
          <div className="flex items-center gap-2 text-violet-800 font-medium text-sm">
            <Sparkles className="w-4 h-4" /> Estrazione automatica dati (facoltativa)
          </div>
          <p className="text-xs text-violet-700">
            Carica il documento d'identità: i campi sotto si compilano da soli (puoi comunque correggerli a mano).
            Il documento viene allegato in anagrafica quando crei il cliente.
            {nc.is_company ? ' Per un\'azienda: visura camerale e, se vuoi, il documento del referente.' : ''}
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Select value={docTipo} onValueChange={setDocTipo}>
              <SelectTrigger className="bg-white sm:w-56"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DOC_TIPI_OCR.map((t) => <SelectItem key={t.ocr} value={t.ocr}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <label className={`flex-1 flex items-center justify-center gap-2 border border-dashed border-violet-300 rounded-lg py-2 text-sm text-violet-700 hover:bg-violet-100 cursor-pointer ${estraendo ? 'opacity-60 pointer-events-none' : ''}`}>
              {estraendo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {estraendo ? 'Lettura dati...' : `Carica ${DOC_TIPI_OCR.find((t) => t.ocr === docTipo).label}`}
              <input
                type="file"
                accept="application/pdf,image/jpeg,image/png"
                className="hidden"
                onChange={(e) => { estraiDaDocumento(e.target.files?.[0]); e.target.value = ''; }}
              />
            </label>
          </div>
          {documenti.length > 0 && (
            <p className="text-xs text-violet-700">Da allegare: {documenti.map((d) => d.sigla).join(', ')}</p>
          )}
        </div>
      )}

      {esistente && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 rounded-lg border border-amber-300 bg-amber-50 text-sm text-amber-800">
          <span>
            Codice fiscale già registrato: <b>{esistente.last_name} {esistente.first_name}</b>
          </span>
          <Button type="button" size="sm" onClick={usaEsistente}>Usa questo cliente</Button>
        </div>
      )}

      {nc.is_company ? (
        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-2 space-y-1">
            <Label className="text-xs">Ragione sociale *</Label>
            <Input value={nc.company_name} onChange={(e) => setNc({ ...nc, company_name: e.target.value.toUpperCase() })} className="uppercase" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">P. IVA</Label>
            <Input value={nc.vat_number} onChange={(e) => setNc({ ...nc, vat_number: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">PEC</Label>
            <Input value={nc.pec} onChange={(e) => setNc({ ...nc, pec: e.target.value.toLowerCase() })} />
          </div>
          <div className="col-span-2 text-xs font-medium text-blue-700 pt-1">Sede legale</div>
          <div className="col-span-2 grid grid-cols-4 gap-2">
            <div className="col-span-3 space-y-1">
              <Label className="text-xs">Via</Label>
              <Input value={nc.company_address} onChange={(e) => setNc({ ...nc, company_address: e.target.value.toUpperCase() })} className="uppercase" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Civico</Label>
              <Input value={nc.company_address_number} onChange={(e) => setNc({ ...nc, company_address_number: e.target.value.toUpperCase() })} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">CAP</Label>
              <Input value={nc.company_postal_code} onChange={(e) => setNc({ ...nc, company_postal_code: e.target.value.replace(/\D/g, '').slice(0, 5) })} />
            </div>
            <div className="col-span-2 space-y-1">
              <Label className="text-xs">Comune</Label>
              <Input value={nc.company_city} onChange={(e) => setNc({ ...nc, company_city: e.target.value.toUpperCase() })} className="uppercase" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Prov.</Label>
              <Input value={nc.company_provincia} maxLength={2} onChange={(e) => setNc({ ...nc, company_provincia: e.target.value.toUpperCase() })} className="uppercase" />
            </div>
          </div>
          <div className="col-span-2 text-xs font-medium text-blue-700 pt-1">Referente / legale rappresentante</div>
          <div className="space-y-1">
            <Label className="text-xs">Cognome</Label>
            <Input value={nc.last_name} onChange={(e) => setNc({ ...nc, last_name: e.target.value.toUpperCase() })} className="uppercase" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Nome</Label>
            <Input value={nc.first_name} onChange={(e) => setNc({ ...nc, first_name: e.target.value.toUpperCase() })} className="uppercase" />
          </div>
          <div className="col-span-2 space-y-1">
            <Label className="text-xs">Codice Fiscale referente</Label>
            <Input value={nc.fiscal_code} onChange={(e) => setNc({ ...nc, fiscal_code: e.target.value.toUpperCase() })} className="uppercase font-mono" />
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Cognome *</Label>
              <Input value={nc.last_name} onChange={(e) => setNc({ ...nc, last_name: e.target.value.toUpperCase() })} className="uppercase" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Nome</Label>
              <Input value={nc.first_name} onChange={(e) => setNc({ ...nc, first_name: e.target.value.toUpperCase() })} className="uppercase" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Sesso</Label>
              <Select value={nc.gender || undefined} onValueChange={(v) => setNc({ ...nc, gender: v })}>
                <SelectTrigger className="h-9"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="M">M</SelectItem>
                  <SelectItem value="F">F</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1">
              <Label className="text-xs">Data di nascita</Label>
              <DateInput value={nc.birth_date} onChange={(val) => setNc({ ...nc, birth_date: val })} placeholder="gg/mm/aaaa" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={nc.birth_abroad} onChange={(e) => setNc({ ...nc, birth_abroad: e.target.checked })} />
            Nato/a all'estero
          </label>
          {nc.birth_abroad ? (
            <div className="space-y-1">
              <Label className="text-xs">Nazione di nascita</Label>
              <Input value={nc.birth_country} onChange={(e) => setNc({ ...nc, birth_country: e.target.value.toUpperCase() })} className="uppercase" placeholder="es. CINA" />
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Comune di nascita</Label>
                <BirthComuneInput
                  value={nc.birth_comune}
                  onSelect={(nome, prov) => setNc((x) => ({ ...x, birth_comune: nome, ...(prov != null ? { birth_provincia: prov } : {}) }))}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Prov.</Label>
                <Input value={nc.birth_provincia} maxLength={2}
                  onChange={(e) => setNc({ ...nc, birth_provincia: e.target.value.toUpperCase() })} className="uppercase" />
              </div>
            </div>
          )}
          <div className="space-y-1">
            <Label className="text-xs">Codice Fiscale</Label>
            <div className="flex gap-1">
              <Input value={nc.fiscal_code} onChange={(e) => setNc({ ...nc, fiscal_code: e.target.value.toUpperCase() })} className="uppercase font-mono" />
              <Button type="button" size="sm" variant="outline" onClick={calcCF} data-testid="new-client-calc-cf">Calcola CF</Button>
            </div>
          </div>
        </div>
      )}

      {/* Indirizzo — Google Maps */}
      <div className="grid grid-cols-4 gap-2">
        <div className="col-span-3 space-y-1">
          <Label className="text-xs">Indirizzo <span className="text-blue-600">(Google Maps)</span></Label>
          {GOOGLE_MAPS_API_KEY ? (
            <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
              <AddressAutocomplete
                value={nc.address}
                defaultValue={nc.address}
                placeholder="es. Via Roma, Piazza Duomo..."
                onPlaceSelect={(d) => setNc((x) => ({
                  ...x,
                  address: (d.address || x.address || '').toUpperCase(),
                  address_number: (d.address_number || x.address_number || '').toUpperCase(),
                  city: (d.city || x.city || '').toUpperCase(),
                  city_provincia: (d.city_provincia || x.city_provincia || '').toUpperCase(),
                  postal_code: d.postal_code || x.postal_code,
                }))}
              />
            </APIProvider>
          ) : (
            <Input value={nc.address} onChange={(e) => setNc({ ...nc, address: e.target.value.toUpperCase() })} className="uppercase" />
          )}
        </div>
        <div className="space-y-1">
          <Label className="text-xs">N. civ.</Label>
          <Input value={nc.address_number} onChange={(e) => setNc({ ...nc, address_number: e.target.value })} />
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2">
        <div className="col-span-2 space-y-1">
          <Label className="text-xs">Città</Label>
          <Input value={nc.city} onChange={(e) => setNc({ ...nc, city: e.target.value.toUpperCase() })} className="uppercase" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Prov.</Label>
          <Input value={nc.city_provincia} maxLength={2}
            onChange={(e) => setNc({ ...nc, city_provincia: e.target.value.toUpperCase() })} className="uppercase" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">CAP</Label>
          <Input value={nc.postal_code} maxLength={5}
            onChange={(e) => setNc({ ...nc, postal_code: e.target.value.replace(/\D/g, '') })} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-xs">Cellulare</Label>
          <Input value={nc.phone} onChange={(e) => setNc({ ...nc, phone: e.target.value })} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Email</Label>
          <Input type="email" value={nc.email} onChange={(e) => setNc({ ...nc, email: e.target.value })} />
        </div>
      </div>

      {/* Consensi privacy */}
      <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-emerald-800 flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4" /> Consensi privacy
          </span>
          <button type="button" onClick={toggleInformativa} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
            <FileText className="w-3.5 h-3.5" /> Leggi informativa
          </button>
        </div>
        {showInformativa && informativa && (
          <pre className="text-[11px] leading-relaxed bg-white border rounded p-2 max-h-44 overflow-y-auto whitespace-pre-wrap text-slate-600">
            {informativa.text}
          </pre>
        )}
        {CONSENT_FIELDS.map((c) => (
          <label key={c.key} className="flex items-start gap-2 text-xs text-slate-700">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={c.required ? true : consents[c.key]}
              disabled={c.required}
              onChange={(e) => setConsents((x) => ({ ...x, [c.key]: e.target.checked }))}
            />
            <span>{c.label}{c.required && <span className="text-slate-400"> (necessario)</span>}</span>
          </label>
        ))}
        <p className="text-[11px] text-slate-400">Data, ora e versione dell'informativa vengono registrate automaticamente.</p>
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="outline" onClick={onCancel}>Annulla</Button>
        <Button type="button" size="sm" onClick={handleCreate} disabled={saving} data-testid="save-new-client">
          {saving ? 'Salvataggio…' : 'Crea e collega'}
        </Button>
      </div>
      <p className="text-[11px] text-slate-400">Da qui puoi solo registrare un nuovo cliente. Per modificarlo, vai in Anagrafica Clienti.</p>
    </div>
  );
}
